import { useFormContext, get } from 'react-hook-form';
import { Box } from '@repo/ui/Box';
import { Flex } from '@repo/ui/Flex';
import { InputControl } from '@repo/ui/Input/InputControl';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputRoot } from '@repo/ui/Input/InputRoot';
import { Button } from '@repo/ui/Button';
import { AddressForm } from '../../AddressForm/AddressForm';
import { getLocale } from '../../../utils/locale';
import { rawString, Text } from '../../Language/Language';
import type { TaskComponentType } from '../../../workflow/schemaTypes';
import s from './OrderlessTask.module.css';
import { InputMessage } from '@repo/ui/Input/InputMessage';

export const OrderlessTask: TaskComponentType = ({ handleNext, isLoading }) => {
  const { register, formState } = useFormContext();
  const { errors } = formState;
  const { country } = getLocale();

  return (
    <Box isContainer className={s.wrapper}>
      <form onSubmit={handleNext}>
        <Flex direction="column" gap="g-24">
          <h2 className={s.title}>
            <Text langKey="pdx.orderless.title" />
          </h2>
          <p className={s.description}>
            <Text langKey="pdx.orderless.description" />
          </p>

          <Flex direction="column" gap="g-16">
            <Flex direction="column" gap="g-12">
              <h3 className={s.sectionTitle}>
                <Text langKey="pdx.orderless.contact_information" />
              </h3>
              <Flex direction="row" gap="g-12" className={s.rowFields}>
                <InputRoot
                  width="full"
                  variant={
                    get(errors, 'orderlessCustomer.firstName')
                      ? 'error'
                      : 'default'
                  }
                >
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="orderlessCustomer.firstName">
                        {rawString({
                          langKey: [
                            'pdx.orderless.first_name',
                            'pdx.global.first_name',
                          ],
                        })}
                      </InputLabel>
                      <InputControl
                        id="orderlessCustomer.firstName"
                        type="text"
                        {...register('orderlessCustomer.firstName')}
                      />
                    </InputField>
                  </InputGroup>
                  {get(errors, 'orderlessCustomer.firstName') && (
                    <InputMessage>
                      {get(errors, 'orderlessCustomer.firstName')?.message}
                    </InputMessage>
                  )}
                </InputRoot>
                <InputRoot
                  width="full"
                  variant={
                    get(errors, 'orderlessCustomer.lastName')
                      ? 'error'
                      : 'default'
                  }
                >
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="orderlessCustomer.lastName">
                        {rawString({
                          langKey: [
                            'pdx.orderless.last_name',
                            'pdx.global.last_name',
                          ],
                        })}
                      </InputLabel>
                      <InputControl
                        id="orderlessCustomer.lastName"
                        type="text"
                        {...register('orderlessCustomer.lastName')}
                      />
                    </InputField>
                  </InputGroup>
                  {get(errors, 'orderlessCustomer.lastName') && (
                    <InputMessage>
                      {get(errors, 'orderlessCustomer.lastName')?.message}
                    </InputMessage>
                  )}
                </InputRoot>
              </Flex>
              <InputRoot
                width="full"
                variant={
                  get(errors, 'orderlessCustomer.email') ? 'error' : 'default'
                }
              >
                <InputGroup>
                  <InputField>
                    <InputLabel htmlFor="orderlessCustomer.email">
                      {rawString({
                        langKey: ['pdx.orderless.email', 'pdx.global.email'],
                      })}
                    </InputLabel>
                    <InputControl
                      id="orderlessCustomer.email"
                      type="email"
                      {...register('orderlessCustomer.email')}
                    />
                  </InputField>
                </InputGroup>
                {get(errors, 'orderlessCustomer.email') && (
                  <InputMessage>
                    {get(errors, 'orderlessCustomer.email')?.message}
                  </InputMessage>
                )}
              </InputRoot>
              <Flex direction="row" gap="g-12" className={s.rowFields}>
                <InputRoot
                  width="full"
                  variant={
                    get(errors, 'orderlessCustomer.phone') ? 'error' : 'default'
                  }
                >
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="orderlessCustomer.phone">
                        {rawString({
                          langKey: ['pdx.orderless.phone', 'pdx.global.phone'],
                        })}
                      </InputLabel>
                      <InputControl
                        id="orderlessCustomer.phone"
                        type="tel"
                        {...register('orderlessCustomer.phone')}
                      />
                    </InputField>
                  </InputGroup>
                  {get(errors, 'orderlessCustomer.phone') && (
                    <InputMessage>
                      {get(errors, 'orderlessCustomer.phone')?.message}
                    </InputMessage>
                  )}
                </InputRoot>
                <InputRoot
                  width="full"
                  variant={
                    get(errors, 'orderlessCustomer.phoneExtension')
                      ? 'error'
                      : 'default'
                  }
                >
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="orderlessCustomer.phoneExtension">
                        {rawString({
                          langKey: [
                            'pdx.orderless.phone_extension',
                            'pdx.global.phone_extension',
                          ],
                        })}
                      </InputLabel>
                      <InputControl
                        id="orderlessCustomer.phoneExtension"
                        {...register('orderlessCustomer.phoneExtension')}
                      />
                    </InputField>
                  </InputGroup>
                  {get(errors, 'orderlessCustomer.phoneExtension') && (
                    <InputMessage>
                      {get(errors, 'orderlessCustomer.phoneExtension')?.message}
                    </InputMessage>
                  )}
                </InputRoot>
              </Flex>
            </Flex>

            {/* Shipping Address */}
            <AddressForm
              name="orderlessCustomer.address"
              countryCode={country}
              baseLabel="pdx.delivery.address"
            />
          </Flex>

          {/* Submit Button */}
          <Box margin="mt-32">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isLoading}
            >
              <Text langKey="pdx.orderless.submit_button" />
            </Button>
          </Box>
        </Flex>
      </form>
    </Box>
  );
};
